import React, { useMemo } from 'react';
import { X } from 'lucide-react';

const formatNumber = (value, maximumFractionDigits = 5) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '—';
    return number.toLocaleString(undefined, { maximumFractionDigits });
};

const DepthRows = ({ levels, side, maxVolume }) => (
    <div className="space-y-px">
        {levels.map((level, index) => {
            const volume = Number(level?.volume) || 0;
            const width = maxVolume > 0 ? Math.min(100, (volume / maxVolume) * 100) : 0;
            const isAsk = side === 'ask';
            return (
                <div
                    key={`${side}-${level?.price}-${index}`}
                    className="relative grid grid-cols-[1fr_0.8fr] items-center px-2 py-1 text-[11px] tabular-nums"
                >
                    <div
                        className={`absolute inset-y-0 ${isAsk ? 'right-0 bg-rose-500/15' : 'left-0 bg-emerald-500/15'}`}
                        style={{ width: `${width}%` }}
                    />
                    <span className={`relative font-medium ${isAsk ? 'text-rose-300' : 'text-emerald-300'}`}>
                        {formatNumber(level?.price, 8)}
                    </span>
                    <span className="relative text-right text-slate-300">
                        {formatNumber(volume, 4)}
                    </span>
                </div>
            );
        })}
    </div>
);

const MarketDepthPanel = ({ snapshot, status, error, onClose }) => {
    const bids = Array.isArray(snapshot?.bids) ? snapshot.bids : [];
    const asks = Array.isArray(snapshot?.asks) ? snapshot.asks : [];
    const displayAsks = useMemo(() => [...asks].slice(0, 12).reverse(), [asks]);
    const displayBids = useMemo(() => bids.slice(0, 12), [bids]);
    const maxVolume = useMemo(
        () => Math.max(0, ...displayAsks.map((level) => Number(level?.volume) || 0), ...displayBids.map((level) => Number(level?.volume) || 0)),
        [displayAsks, displayBids],
    );
    const bestBid = displayBids[0]?.price;
    const bestAsk = asks[0]?.price;
    const spread = Number.isFinite(Number(bestBid)) && Number.isFinite(Number(bestAsk))
        ? Number(bestAsk) - Number(bestBid)
        : null;

    return (
        <aside className="pointer-events-auto absolute bottom-3 right-3 top-14 z-40 flex w-[260px] flex-col overflow-hidden rounded-lg border border-slate-700/80 bg-slate-950/92 shadow-2xl backdrop-blur">
            <div className="flex items-center justify-between border-b border-slate-700/70 px-3 py-2">
                <div>
                    <div className="text-xs font-semibold text-slate-100">Depth of Market</div>
                    <div className="text-[10px] text-slate-400">
                        {snapshot?.symbol || '—'} · {snapshot?.platform || snapshot?.source || 'Broker'}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
                    aria-label="Close depth of market"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>

            <div className="grid grid-cols-[1fr_0.8fr] border-b border-slate-800 px-2 py-1 text-[9px] uppercase tracking-wide text-slate-500">
                <span>Price</span><span className="text-right">Volume</span>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto py-1">
                {error ? (
                    <div className="m-3 rounded border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-200">
                        {error}
                    </div>
                ) : status === 'subscribing' && !snapshot ? (
                    <div className="p-4 text-center text-xs text-slate-400">Connecting to broker DOM…</div>
                ) : bids.length === 0 && asks.length === 0 ? (
                    <div className="p-4 text-center text-xs text-slate-400">No market-depth levels supplied for this symbol.</div>
                ) : (
                    <>
                        <DepthRows levels={displayAsks} side="ask" maxVolume={maxVolume} />
                        <div className="my-1 flex items-center justify-between border-y border-slate-700/70 bg-slate-900/80 px-2 py-1.5 text-[10px] text-slate-300">
                            <span>Spread</span>
                            <span className="font-semibold tabular-nums">{formatNumber(spread, 8)}</span>
                        </div>
                        <DepthRows levels={displayBids} side="bid" maxVolume={maxVolume} />
                    </>
                )}
            </div>

            <div className="border-t border-slate-800 px-2 py-1 text-[9px] text-slate-500">
                Broker-specific snapshot · {snapshot?.depth ?? 0} levels
            </div>
        </aside>
    );
};

export default MarketDepthPanel;
